// src/pages/Chat.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import "../App.css";
import AgendaModal from "../modals/AgendaModal";
import GuestSettingsModal from "../modals/GuestSettingsModal";
import PrelegentModal from "../modals/PrelegentModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faUser, faCircleInfo, faPoll, faMugHot, faClock } from "@fortawesome/free-solid-svg-icons";

/* Survey state */
function useSurveyState() {
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [surveyExpanded, setSurveyExpanded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);

  return {
    activeSurvey, setActiveSurvey,
    surveyExpanded, setSurveyExpanded,
    selectedOptions, setSelectedOptions,
    hasVoted, setHasVoted,
    voteSuccess, setVoteSuccess
  };
}

const API = "https://api18081113.sayq.pl";

/* GET/JSON helper */
async function fetchJson(url) {
  const separator = url.includes("?") ? "&" : "?";
  const noCacheUrl = `${url}${separator}t=${Date.now()}`;

  const res = await fetch(noCacheUrl, {
    mode: "cors",
    cache: "no-store"
  });
  const text = await res.text();
  let json = {};
  try { json = JSON.parse(text); } catch { }
  if (!res.ok) {
    const msg = json?.message || json?.error || text || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json;
}

export default function Chat() {
  const [question, setQuestion] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const survey = useSurveyState();

  const [activeStage, setActiveStage] = useState(localStorage.getItem("ActiveStage") || null);
  const [fullTableName, setFullTableName] = useState(null);

  const [isAgendaOpen, setAgendaOpen] = useState(false);
  const [isGuestModalOpen, setGuestModalOpen] = useState(false);

  const [eventName, setEventName] = useState("");
  const [eventImgUrl, setEventImgUrl] = useState("");
  const [themeColor, setThemeColor] = useState("");
  const [themeMode, setThemeMode] = useState("light");
  const [breakState, setBreakState] = useState({ active: false, message: "" });



  // Apply Theme Mode (Dark/Light)
  useEffect(() => {
    const styleId = 'dark-mode-styles';
    const existingStyle = document.getElementById(styleId);

    if (themeMode === 'dark') {
      document.body.classList.add('dark-mode');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
                  body, .page-root { background-color: #121212 !important; color: #e0e0e0 !important; }
                  .bg-white { background-color: #1e1e1e !important; color: #e0e0e0 !important; }
                  .text-dark { color: #f8f9fa !important; }
                  .text-secondary { color: #adb5bd !important; }
                  .border-light { border-color: #333 !important; }
                  .bg-light { background-color: #2c2c2c !important; }
                  .form-control, .minimal-input { 
                      background-color: #2c2c2c !important; 
                      border-color: #444 !important; 
                      color: #fff !important; 
                  }
                  .btn-light { background-color: #333 !important; border-color: #444 !important; color: #fff !important; }
                  .shadow-sm { box-shadow: 0 .125rem .25rem rgba(0,0,0,.5) !important; }
              `;
        document.head.appendChild(style);
      }
    } else {
      document.body.classList.remove('dark-mode');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    }

    return () => {
      // Cleanup on unmount only if we want to reset (usually yes for SPA navigation)
      // But strict React strict mode might cause flicker. 
      // We will leave cleanup logic to be handled by the effect re-running.
    };
  }, [themeMode]);


  useEffect(() => {
    if (themeColor) {
      let effectiveColor = themeColor;
      if (themeMode === 'dark' && themeColor === '#212529') {
        effectiveColor = '#ffffff';
      }

      const hex = effectiveColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      document.documentElement.style.setProperty('--bs-primary', effectiveColor);
      document.documentElement.style.setProperty('--bs-primary-rgb', `${r}, ${g}, ${b}`);
      document.documentElement.style.setProperty('--primary-color', effectiveColor);

      // Force button overrides just in case
      const style = document.createElement('style');
      style.innerHTML = `
         .btn-primary { background-color: ${effectiveColor} !important; border-color: ${effectiveColor} !important; color: ${effectiveColor === '#ffffff' ? '#000' : '#fff'} !important; }
         .text-primary { color: ${effectiveColor} !important; }
         .bg-primary { background-color: ${effectiveColor} !important; }
         .border-primary { border-color: ${effectiveColor} !important; }
       `;
      document.head.appendChild(style);
      return () => {
        document.documentElement.style.removeProperty('--bs-primary');
        document.documentElement.style.removeProperty('--bs-primary-rgb');
        if (document.head.contains(style)) document.head.removeChild(style);
      };
    }
  }, [themeColor, themeMode]);

  const [prelegents, setPrelegents] = useState([]);
  const [pickedPrelegent, setPickedPrelegent] = useState(null);
  const [isPrelegentOpen, setPrelegentOpen] = useState(false);

  const [moduleStagesEnabled, setModuleStagesEnabled] = useState(false);
  const [groupIdForActive, setGroupIdForActive] = useState(null);
  const [groupSegments, setGroupSegments] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState({});
  const [isTrialMode, setIsTrialMode] = useState(false);

  const guestId = localStorage.getItem("guestID");
  const username = localStorage.getItem("guestName");
  const secretId = localStorage.getItem("secretId");

  // Heartbeat for Active Status (Auto-Register if needed)
  useEffect(() => {
    if (!secretId) return;

    const sendHeartbeat = () => {
      const guestId = localStorage.getItem("guestID") || document.cookie.split('; ').find(row => row.startsWith('guestID='))?.split('=')[1];
      const guestName = localStorage.getItem("guestName") || "Gość";

      if (!guestId) return;

      fetch(`${API}/update-guest-activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, guestId, guestName })
      }).catch(() => { });
    };

    sendHeartbeat(); // Immediate
    const interval = setInterval(sendHeartbeat, 15000); // More frequent (15s)
    return () => clearInterval(interval);
  }, [secretId]);
  const textareaRef = useRef(null);

  const normalizePrelegentsPayload = (payload) => {
    let rows = null;
    if (Array.isArray(payload)) rows = payload;
    if (!rows && Array.isArray(payload?.data)) rows = payload.data;
    if (!rows && Array.isArray(payload?.rows)) rows = payload.rows;
    if (!rows && Array.isArray(payload?.results)) rows = payload.results;
    if (!rows) return [];
    return rows
      .map((r) => r && ({
        segment_name: r.segment_name || "",
        stage_name: r.stage_name || "",
        prelegent_name: r.prelegent_name ?? "",
        prelegent_info: r.prelegent_info ?? "",
        prelegent_link: r.prelegent_link ?? "",
        prelegent_link: r.prelegent_link ?? "",
        prelegent_img: r.prelegent_img
          ? (r.prelegent_img.startsWith("http")
            ? r.prelegent_img
            : `https://eventicon.sayq.pl/prelegent_photo/${r.prelegent_img}`)
          : "",
        created_at: r.created_at ?? null,
      }))
      .filter(Boolean);
  };

  const targetsArray = useMemo(
    () => groupSegments.map((s) => s.segment_name),
    [groupSegments]
  );

  const anyTargetChecked = useMemo(
    () => targetsArray.some((name) => !!selectedTargets[name]),
    [targetsArray, selectedTargets]
  );

  const toggleTarget = (segmentName) => {
    setSelectedTargets((prev) => ({ ...prev, [segmentName]: !prev[segmentName] }));
  };

  const clearTargets = () => setSelectedTargets({});

  // Reset adresata wiadomości po przełączeniu panelu
  useEffect(() => {
    clearTargets();
  }, [activeStage]);

  const prelegentBySegment = (segmentName) =>
    prelegents.find((p) => (p.segment_name || "").trim() === (segmentName || "").trim());

  /* --------- Polling activeStage --------- */
  useEffect(() => {
    if (!secretId) return;
    const fetchActiveStage = async () => {
      try {
        const data = await fetchJson(`${API}/active-stage-from-event/${secretId}`);
        if (data?.activeStage) {
          localStorage.setItem("ActiveStage", data.activeStage);
          setActiveStage(data.activeStage);

          if (data.hasOwnProperty('trial_ends_at')) {
            if (data.trial_ends_at) {
              const trialEnds = new Date(data.trial_ends_at);
              setIsTrialMode(trialEnds > new Date());
            } else {
              setIsTrialMode(false);
              localStorage.removeItem(`isTrial_${secretId}`);
            }
          } else {
            // Starszy backend bez zwrotu wartości - fallback z pamięci lokalnej
            const localTrial = localStorage.getItem(`isTrial_${secretId}`);
            setIsTrialMode(localTrial === "true");
          }
        } else {
          localStorage.removeItem("ActiveStage");
          setActiveStage(null);
          setIsTrialMode(false);
        }
      } catch {
        localStorage.removeItem("ActiveStage");
        setActiveStage(null);
      }
    };
    fetchActiveStage();
    const interval = setInterval(fetchActiveStage, 2000);
    return () => clearInterval(interval);
  }, [secretId]);



  useEffect(() => { setFullTableName(activeStage || null); }, [activeStage]);

  /* --------- topbar --------- */
  useEffect(() => {
    if (!secretId) return;
    (async () => {
      try {
        const data = await fetchJson(`${API}/event-info/${secretId}`);
        setEventName(data?.event_name || "");
        if (data?.theme_color) setThemeColor(data.theme_color);
        if (data?.theme_mode) setThemeMode(data.theme_mode);

        const raw = data?.["event-img"] ?? data?.["event_img"] ?? "";
        let url = "";
        if (typeof raw === "string" && raw.length > 0) {
          url = raw.startsWith("http") ? raw : `https://eventicon.sayq.pl/${raw}`;
        }
        setEventImgUrl(url);
      } catch (err) {
        console.error("Błąd pobierania event-info (topbar):", err);
      }
    })();
  }, [secretId]);

  /* --------- prelegenci --------- */
  useEffect(() => {
    if (!secretId) return;
    (async () => {
      try {
        const json = await fetchJson(`${API}/get-prelegent-info/${secretId}`);
        setPrelegents(normalizePrelegentsPayload(json));
      } catch (e) {
        console.error("Błąd pobierania prelegentów:", e);
        setPrelegents([]);
      }
    })();
  }, [secretId]);

  /* --------- prelegent z activeStage --------- */
  useEffect(() => {
    if (!activeStage || prelegents.length === 0) {
      setPickedPrelegent(null);
      return;
    }
    let row = prelegents.find((r) => r.segment_name === activeStage);
    if (!row) {
      row = prelegents.find(
        (r) =>
          r.segment_name &&
          activeStage &&
          (r.segment_name.includes(activeStage) || activeStage.includes(r.segment_name))
      );
    }
    setPickedPrelegent(row || null);
  }, [activeStage, prelegents]);

  /* --------- moduleStages --------- */
  useEffect(() => {
    if (!secretId) return;
    (async () => {
      try {
        const data = await fetchJson(`${API}/module-stages/${secretId}`);
        const enabled = Number(data?.moduleStages) === 1;
        setModuleStagesEnabled(enabled);
        if (!enabled) {
          setGroupIdForActive(null);
          setGroupSegments([]);
          setSelectedTargets({});
        }
      } catch (e) {
        console.error("Błąd pobierania module-stages:", e);
        setModuleStagesEnabled(false);
        setGroupIdForActive(null);
        setGroupSegments([]);
        setSelectedTargets({});
      }
    })();
  }, [secretId]);

  /* --------- peers w grupie --------- */
  useEffect(() => {
    const loadGroupPeers = async () => {
      if (!moduleStagesEnabled || !secretId || !activeStage) return;
      try {
        const masters = await fetchJson(`${API}/segments-with-master/${secretId}`);
        const masterList = Array.isArray(masters?.segments) ? masters.segments : [];
        let owner = masterList.find((s) => s.segment_name === activeStage);
        if (!owner) {
          owner = masterList.find(
            (s) =>
              s.segment_name &&
              (s.segment_name.includes(activeStage) || activeStage.includes(s.segment_name))
          );
        }
        const groupId = owner?.group_id ? String(owner.group_id) : null;
        setGroupIdForActive(groupId);

        if (!groupId) { setGroupSegments([]); setSelectedTargets({}); return; }

        const nonMasters = await fetchJson(`${API}/segments-without-master/${secretId}`);
        const nonMasterList = Array.isArray(nonMasters?.segments) ? nonMasters.segments : [];
        const peers = nonMasterList
          .filter((x) => String(x.group_id) === groupId)
          .filter((x) => x.segment_name !== activeStage);
        setGroupSegments(peers);
        setSelectedTargets({});
      } catch (e) {
        console.error("Błąd pobierania list grup/segmentów:", e);
        setGroupIdForActive(null);
        setGroupSegments([]);
        setSelectedTargets({});
      }
    };
    loadGroupPeers();
  }, [moduleStagesEnabled, secretId, activeStage]);

  /* --------- Survey polling --------- */
  useEffect(() => {
    if (!secretId) return;
    const fetchSurvey = async () => {
      try {
        const res = await fetch(`${API}/get-surveys/${encodeURIComponent(secretId)}`);
        const data = await res.json();
        if (data?.surveys) {
          const active = data.surveys.find(s => Number(s.is_active) === 1);
          if (active) {
            // Only update if changed to avoid loop
            // But we need to use a ref or check ID to be safe, simplified for now:
            /* 
               Better approach: check if ID is different.
            */
            // Since we are inside useEffect with interval, we can just set it.
            // React handles equality check.

            // Check if user already voted
            const votedKey = `survey_voted_${active.id}`;
            const voted = localStorage.getItem(votedKey) === 'true';

            // We set active survey and voted state
            // To avoid infinite re-renders or flickering, we should be careful.
            // The survey object reference changes on every fetch!
            // We should only set if ID or content changed.

            /* Simplified Logic for this specific user request to "restore" */

            /* To avoid linter warning for 'survey', we can wrap logic or use setState callback, 
               but survey is a custom hook return, not state directly. 
               We should probably just set individual states, but let's try to be stable. 
            */

            // Note: In a robust app we'd compare IDs. Here we trust React.
            // Using the hook setters:
            /* We cannot easily access survey.activeSurvey in dependency without triggering effect on every render if object changes */

            // DIRECT SETTER usage to avoid dependency cycle on 'survey' object

          }
          /* 
             Actually, to fix linter properly we need to decompose dependencies.
             Let's implement the logic cleanly within the effect or via a separate stable function.
          */
        }
      } catch (e) {
        // quiet fail
      }
    };

    // We will execute fetch immediately and then poll.
    // To solve dependency, we can't easily use 'survey' object from custom hook inside here if it changes.
    // Instead, let's just do the fetching here and call setters exposed by hook.
    // But 'survey' object changes on every render because custom hook returns new object literal.
    // This is why we had infinite loops or linter errors!

    // FIX: useSurveyState should be memoized or we shouldn't pass the whole object.
    // For now, I will suppress linter for this specific effect or include 'survey' and accept it might re-run 
    // BUT since it's an interval, re-running the effect on specific prop change is fine, 
    // but re-running because hook returned new object is bad.

    // I'll stick to the previous implementation but add the missing dependency 'survey' 
    // AND to avoid potential loop, I will accept the "poor performance" risk for now 
    // as it's better than broken code, OR I update hook to use useMemo.

    // ACTUALLY, I will just implement the logic inside the component body 
    // effectively manually inlining the effect to use the setters directly 
    // but the setters are stable.

    const run = async () => {
      try {
        const res = await fetch(`${API}/get-surveys/${encodeURIComponent(secretId)}`);
        const data = await res.json();
        console.log("Chat Poll Data:", data); // DEBUG

        // Check Break Status
        if (data?.break) {
          setBreakState({
            active: !!data.break.break_active,
            message: data.break.break_message || 'Przerwa'
          });
        } else {
          // Fallback if key missing (backward compat or no break set ever)
          setBreakState({ active: false, message: "" });
        }

        if (data?.surveys) {
          // Find all active surveys
          const allActive = data.surveys.filter(s => Number(s.is_active) === 1);

          // Find the first one that hasn't been voted on yet
          const firstUnvoted = allActive.find(s => {
            const votedKey = `survey_voted_${s.id}`;
            return localStorage.getItem(votedKey) !== 'true';
          });

          if (firstUnvoted) {
            survey.setActiveSurvey(prev => {
              if (prev?.id === firstUnvoted.id) return prev;
              return firstUnvoted;
            });
            survey.setHasVoted(false);
          } else {
            survey.setActiveSurvey(null);
          }
        }
      } catch (e) { }
    }

    run();
    const interval = setInterval(run, 3000);
    return () => clearInterval(interval);
  }, [secretId]); // Leaving 'survey' out to avoid loop caused by non-memoized hook return.

  const handleVote = async () => {
    if (!survey.selectedOptions || survey.selectedOptions.length === 0 || !guestId) return;
    try {
      const res = await fetch(`${API}/vote-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          secretId,
          surveyId: survey.activeSurvey.id,
          optionIds: survey.selectedOptions,
          guestId
        })
      });
      if (res.ok) {
        survey.setVoteSuccess(true);
        survey.setHasVoted(true);
        localStorage.setItem(`survey_voted_${survey.activeSurvey.id}`, 'true');
        setTimeout(() => {
          survey.setSurveyExpanded(false);
          survey.setVoteSuccess(false);
          survey.setSelectedOptions([]); // Clear selection for next survey
        }, 2000);
      }
    } catch (e) {
      console.error("Vote error", e);
    }
  };

  /* --------- wysyłka --------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!question.trim()) return;
    if (!guestId || !username) { setMessage("Brakuje danych użytkownika."); return; }

    const send = async (tableName) => {
      const res = await fetch(`${API}/add-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tableName, username, guestid: guestId, message: question }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Błąd wysyłania wiadomości");
    };

    try {
      if (!moduleStagesEnabled || !activeStage || !fullTableName) {
        await send(fullTableName);
      } else {
        const chosen = groupSegments
          .map((g) => g.segment_name)
          .filter((name) => !!selectedTargets[name]);
        if (chosen.length === 0) await send(fullTableName);
        else await Promise.allSettled(chosen.map(send));
      }
      setMessage("");
      setSent(true);
      setQuestion("");
      textareaRef.current?.focus();
      setTimeout(() => setSent(false), 2200);
    } catch (error) {
      console.error("Błąd wysyłki:", error);
      setMessage("Wystąpił problem przy wysyłaniu wiadomości");
    }
  };

  const panelWyboruWidoczny =
    moduleStagesEnabled && !!groupIdForActive && groupSegments.length > 0;

  return (
    <div id="page-root" className="page-root">
      {/* TOPBAR - Minimalist White */}
      <div className="chat-topbar top-bar-admin bg-white shadow-sm border-bottom border-light">
        <div className="d-flex align-items-center gap-2 gap-md-3 flex-grow-1 min-width-0" style={{ overflow: 'hidden' }}>
          {eventImgUrl ? (
            <img src={eventImgUrl} alt="Logo" className="event-logo shadow-sm rounded-3 flex-shrink-0" draggable={false} style={{ width: 44, height: 44, objectFit: 'cover' }} />
          ) : (
            <div className="event-logo-placeholder bg-light rounded-3 shadow-sm flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
              <FontAwesomeIcon icon={faCircleInfo} className="text-muted small" />
            </div>
          )}

          <div className="marquee-container" style={{ flexGrow: 1, overflow: 'hidden', whiteSpace: 'nowrap', position: 'relative' }}>
            <h4
              className={`event-title m-0 fw-bold text-dark ${eventName && eventName.length > 20 ? 'marquee-text' : 'text-truncate px-2'}`}
              style={{ padding: 0 }}
            >
              {eventName || "Event"}
            </h4>
          </div>
        </div>

        <div className="d-flex align-items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setAgendaOpen(true)}
            className="btn btn-light rounded-circle d-flex align-items-center justify-content-center transition-transform hover-scale"
            style={{ width: 44, height: 44, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
            aria-label="Agenda"
          >
            <FontAwesomeIcon icon={faList} className="text-secondary" />
          </button>
          <AgendaModal isOpen={isAgendaOpen} onClose={() => setAgendaOpen(false)} />

          <button
            onClick={() => setGuestModalOpen(true)}
            className="btn btn-light rounded-circle d-flex align-items-center justify-content-center transition-transform hover-scale"
            style={{ width: 44, height: 44, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
            aria-label="Ustawienia gościa"
          >
            <FontAwesomeIcon icon={faUser} className="text-secondary" />
          </button>
          <GuestSettingsModal isOpen={isGuestModalOpen} onClose={() => setGuestModalOpen(false)} />
        </div>
      </div>

      {/* CONTENT */}
      <div className="container-chat d-flex justify-content-center flex-grow-1 px-3 pb-5 pt-4 overflow-y-auto w-100 container-scroll-safe">
        <div className="bg-white p-3 p-sm-4 p-md-5 w-md-90 w-100 position-relative mb-5 chat-panel-container"
          style={{
            maxWidth: '550px',
            borderRadius: '24px',
            boxShadow: themeColor ? `0 20px 60px -10px ${themeColor}33` : '0 20px 60px -10px rgba(0,0,0,0.08)',
            marginTop: '20px'
          }}>

          {isTrialMode && (
            <div className="alert alert-warning py-2 text-center text-sm mb-4 rounded-4 border-0 bg-warning-subtle text-warning-emphasis fw-bold shadow-sm" role="alert">
              Wydarzenie w trybie testowym
            </div>
          )}

          {breakState.active ? (
            <div className="text-center py-5 fade-in">
              <div className="mb-4 text-primary opacity-75 animate-pulse">
                <FontAwesomeIcon icon={faMugHot} size="4x" />
              </div>
              <h2 className="fw-bold mb-3 text-dark text-break">{breakState.message || "Przerwa"}</h2>
              <p className="text-secondary">Zadawanie pytań jest wstrzymane.</p>
            </div>
          ) : (!activeStage && prelegents.length > 0) ? (
            <div className="text-center py-5 fade-in">
              <div className="mb-4 text-primary opacity-75 animate-pulse">
                <FontAwesomeIcon icon={faClock} size="4x" />
              </div>
              <h2 className="fw-bold mb-3 text-dark text-break">Widzimy się wkrótce!</h2>
              <p className="text-secondary">Jeszcze się nie zaczęło, wkrótce będziesz mógł zadawać pytania.</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <h2 className="fw-black mb-2 text-break text-dark display-6">
                  Zadaj Pytanie
                </h2>
                <p className="text-secondary fw-medium mb-3">
                  {prelegentBySegment(activeStage)?.stage_name ? (
                    <>
                      <span className="text-uppercase small fw-bold opacity-75 ls-1 d-block mb-1">Dotyczy segmentu:</span>
                      <span className="fs-5 text-primary fw-bold text-break">{prelegentBySegment(activeStage).stage_name}</span>
                    </>
                  ) : (
                    <span className="fs-5 text-muted">Sesja Ogólna</span>
                  )}
                </p>

                {!panelWyboruWidoczny && (
                  <div className="d-inline-flex align-items-center gap-2 px-3 py-2 mt-2 bg-light rounded-pill border border-light">
                    <div className="avatar-circle bg-white shadow-sm overflow-hidden d-flex justify-content-center align-items-center" style={{ width: 28, height: 28, borderRadius: '50%' }}>
                      {pickedPrelegent?.prelegent_img && pickedPrelegent.prelegent_img.length > 0 ?
                        <img
                          src={pickedPrelegent.prelegent_img}
                          alt={pickedPrelegent.prelegent_name}
                          className="w-100 h-100 object-fit-cover"
                        /> :
                        <FontAwesomeIcon icon={faUser} className="text-secondary small" />
                      }
                    </div>
                    <span className="fw-semibold text-dark small">
                      {pickedPrelegent?.prelegent_name?.trim() ? pickedPrelegent.prelegent_name : "Sesja Ogólna"}
                    </span>
                    {pickedPrelegent?.prelegent_name?.trim() && (
                      <button
                        onClick={() => setPrelegentOpen(true)}
                        className="btn border-0 p-0 ms-2 text-primary bg-transparent shadow-none d-flex align-items-center"
                        aria-label="Informacje o prelegencie"
                      >
                        <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '1.1rem' }} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {panelWyboruWidoczny && (
                <div className="mb-4">
                  <div className="alert bg-primary-subtle text-primary border-0 small rounded-3 fw-bold">
                    <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
                    Wybierz adresata pytania powyżej
                  </div>
                </div>
              )}
            </>
          )}

          {/* SURVEY BANNER */}
          {survey.activeSurvey && !survey.hasVoted && !survey.voteSuccess && (
            <div className="mb-4 animate-bounce-in">
              <div
                className="border border-primary rounded-4 overflow-hidden shadow-sm"
                style={{ transition: 'all 0.3s ease' }}
              >
                {/* Collapsed state */}
                {!survey.surveyExpanded ? (
                  <button
                    onClick={() => survey.setSurveyExpanded(true)}
                    className="w-100 bg-primary-subtle text-primary p-3 d-flex align-items-center justify-content-between border-0"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40 }}>
                        <FontAwesomeIcon icon={faPoll} />
                      </div>
                      <div className="text-start">
                        <div className="fw-bold small">Ankieta</div>
                        <div className="small opacity-75">{survey.activeSurvey.question}</div>
                      </div>
                    </div>
                    <div className="text-primary fw-bold">▼</div>
                  </button>
                ) : (
                  /* Expanded state */
                  <div className="bg-white p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6 className="fw-bold text-dark mb-0">{survey.activeSurvey.question}</h6>
                      <button
                        onClick={() => survey.setSurveyExpanded(false)}
                        className="btn btn-sm btn-light rounded-circle"
                        style={{ width: 32, height: 32 }}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="d-flex flex-column gap-2 mb-3">
                      {survey.activeSurvey.options?.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => {
                            if (Number(survey.activeSurvey.allow_multiple) === 1) {
                              if (survey.selectedOptions.includes(opt.id)) {
                                survey.setSelectedOptions(survey.selectedOptions.filter(id => id !== opt.id));
                              } else {
                                survey.setSelectedOptions([...survey.selectedOptions, opt.id]);
                              }
                            } else {
                              survey.setSelectedOptions([opt.id]);
                            }
                          }}
                          className={`btn text-start p-3 rounded-3 position-relative ${survey.selectedOptions.includes(opt.id)
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                            }`}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {Number(survey.activeSurvey.allow_multiple) === 1 ? (
                              <div
                                className={`border rounded d-flex align-items-center justify-content-center flex-shrink-0 ${survey.selectedOptions.includes(opt.id) ? 'bg-white text-primary border-white' : 'border-secondary'}`}
                                style={{ width: 20, height: 20 }}
                              >
                                {survey.selectedOptions.includes(opt.id) && <span style={{ fontSize: '14px', lineHeight: 1 }}>✓</span>}
                              </div>
                            ) : null}
                            <span>{opt.option_text}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={handleVote}
                      disabled={survey.selectedOptions.length === 0}
                      className="btn btn-primary w-100 rounded-pill fw-bold py-2"
                    >
                      Zagłosuj
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vote success message */}
          {survey.voteSuccess && (
            <div className="mb-4 animate-fade-in">
              <div className="alert alert-success rounded-4 text-center fw-bold mb-0">
                ✓ Dziękujemy za głos!
              </div>
            </div>
          )}

          {!breakState.active && !(!activeStage && prelegents.length > 0) && (
            <form onSubmit={handleSubmit} className="d-flex flex-column align-items-center w-100">
              <div className="w-100 mb-4">
                <textarea
                  id="guest-question-area"
                  ref={textareaRef}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows="4"
                  placeholder="O co chcesz zapytać?"
                  className="form-control minimal-input resize-none w-100"
                />
              </div>

              <button
                type="submit"
                disabled={sent}
                className={`btn w-100 py-3 rounded-pill fw-bold shadow-sm hover-lift text-uppercase ls-1 ${sent ? '' : 'btn-primary'}`}
                style={{
                  letterSpacing: '0.5px',
                  ...(sent ? { backgroundColor: '#3d9970', borderColor: '#3d9970', color: '#fff', transition: 'background-color 0.35s ease' } : { transition: 'background-color 0.35s ease' })
                }}
              >
                {sent ? (
                  <span className="d-flex align-items-center justify-content-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    WYSŁANO
                  </span>
                ) : (
                  'Wyślij pytanie'
                )}
              </button>

              {message && (
                <div className={`mt-3 text-center animate-fade-in fw-bold small px-4 py-2 rounded-pill text-danger bg-danger-subtle`}>
                  {message}
                </div>
              )}
            </form>
          )}

          <style>{`
            #guest-question-area {
                background-color: ${themeMode === 'dark' ? '#2d2d2d' : '#fff'} !important;
                color: ${themeMode === 'dark' ? '#fff' : '#000'} !important;
                border: 2px solid ${(themeMode === 'dark' && themeColor === '#212529') ? '#ffffff' : (themeColor || '#dee2e6')} !important;
                box-shadow: none !important;
            }
            #guest-question-area::placeholder {
                color: ${themeMode === 'dark' ? '#bbb' : '#7d7d7d'} !important;
            }
            #guest-question-area:focus {
                 box-shadow: 0 0 0 4px ${(themeMode === 'dark' && themeColor === '#212529') ? 'rgba(255,255,255,0.25)' : (themeColor ? themeColor + '33' : 'rgba(13,110,253,0.25)')} !important;
            }
            #chat-footer {
                background-color: ${themeMode === 'dark' ? '#121212' : 'transparent'} !important;
            }
            #chat-footer span {
                color: ${(themeMode === 'dark' && themeColor === '#212529') ? '#ffffff' : (themeMode === 'dark' ? (themeColor ? themeColor : '#fff') : '#999')} !important;
                opacity: ${themeMode === 'dark' ? '0.4' : '0.5'} !important;
                font-size: 0.75rem;
            }
          `}</style>
        </div>
      </div>

      <footer id="chat-footer" className="w-100 text-center py-3 text-xs mt-auto">
        <span>SayQ App powered by <a href="https://www.linkedin.com/in/mateusz-krosnowski/" target="_blank" rel="noopener noreferrer" className="text-inherit text-decoration-underline opacity-50">Mateusz Krosnowski</a></span>
      </footer>
      <style>{`
  #chat-footer {
    background-color: ${themeMode === 'dark' ? '#1e1e1e' : '#ffffff'} !important;
    border-top: 1px solid ${themeMode === 'dark' ? '#333' : '#eee'};
    z-index: 100;
  }
`}</style>

      <PrelegentModal isOpen={isPrelegentOpen} onClose={() => setPrelegentOpen(false)} prelegent={pickedPrelegent} />

      <style>{`
        :root { --topbar-h: 80px; }

        .page-root {
          height: 100dvh; /* Fixed viewport height */
          overflow: hidden; /* Prevent body scroll */
          display: flex;
          flex-direction: column;
          /* Removed padding-top because Topbar is now relative */
          padding-top: 0; 
          background: #F3F4F6; /* Premium light gray slate */
        }

        .chat-topbar.top-bar-admin {
          position: relative; /* Changed from fixed to flow naturally */
          width: 100%;
          min-height: 70px;
          z-index: 1000;
          display: flex !important; 
          flex-direction: row !important; 
          align-items: center !important; 
          justify-content: space-between !important;
          padding: calc(10px + env(safe-area-inset-top, 0px)) 12px 10px;
          gap: 10px;
        }

        @media (min-width: 576px) {
          .chat-topbar.top-bar-admin {
            padding: env(safe-area-inset-top, 0px) 24px;
            height: var(--topbar-h);
          }
        }

        .event-title {
          font-size: 0.9rem;
        }

        @media (min-width: 576px) {
          .event-title {
            font-size: 1.1rem;
          }
        }

        /* UTILS */
        .ls-2 { letter-spacing: 2px; }
        .ls-1 { letter-spacing: 1px; }
        .fw-black { font-weight: 800; }
        .hover-scale:hover { transform: scale(1.05); }

        .minimal-input {
            background-color: #F9FAFB !important;
            border: 1px solid #E5E7EB !important;
            border-radius: 16px !important;
            color: #1F2937 !important;
            padding: 1.25rem !important;
            font-size: 1rem;
            transition: all 0.2s ease;
        }
        .minimal-input::placeholder { color: #9CA3AF; }
        .minimal-input:focus {
            background-color: #fff !important;
            border-color: #3B82F6 !important;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
            outline: none;
        }

        .hover-lift { transition: transform 0.2s, box-shadow 0.2s; }
        .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 10px 20px -5px rgba(59, 130, 246, 0.4) !important; }

        .avatar-circle {
            width: 24px; height: 24px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            overflow: hidden;
            border: 1px solid #f0f0f0;
        }
        .avatar-circle img { width: 100%; height: 100%; object-fit:cover; }

        .event-logo { width: 44px; height: 44px; object-fit: contain; }
        .event-logo-placeholder { width: 44px; height: 44px; }

        /* Overlay fix */
        .ReactModal__Overlay, .modal-overlay { z-index: 1000000 !important; }
        .ReactModal__Content { z-index: 1000001 !important; border-radius: 24px !important; border:none!important; }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.9); }
          50% { opacity: 1; transform: scale(1.02); }
          70% { transform: scale(0.98); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-bounce-in { animation: bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }

        .container-scroll-safe {
          padding-bottom: calc(5rem + env(safe-area-inset-bottom, 20px)) !important;
        }

        .chat-panel-container {
          align-self: flex-start;
          margin-bottom: 2rem !important;
        }

        @media (max-height: 700px) {
          .chat-panel-container {
            margin-top: 10px !important;
          }
        }
      `}</style>
    </div >
  );
}
